<template>
    <div class="tm-scope">
        <section class="tm-section--tight oc-head">
            <div class="tm-container">
                <h1 class="oc-title">Occupations</h1>
                <div class="oc-filters">
                    <input v-model="query" class="oc-input" type="search" placeholder="Search a job title or alias…"
                           aria-label="Search occupations" @input="debouncedLoad">
                    <select v-model="category" class="oc-input oc-select tm-select" aria-label="Category" @change="load(1)">
                        <option value="">All categories</option>
                        <option v-for="c in categories" :key="c.name" :value="c.name">{{ c.name }}</option>
                    </select>
                    <LocationSelect v-model="location" class="oc-input oc-select" show-counts @change="onLocation" />
                </div>
            </div>
        </section>

        <section class="tm-container oc-body">
            <p class="oc-count" v-if="!loading">{{ total.toLocaleString() }} occupations</p>
            <div v-if="loading" class="oc-empty">Loading…</div>
            <div v-else-if="!items.length" class="oc-empty">No occupation found.</div>
            <div class="oc-grid">
                <router-link v-for="o in items" :key="o.occupation_id" class="tm-card tm-card--interactive oc-card"
                             :to="{ name: 'occupation', params: { id: o.occupation_id }, query: locQuery }">
                    <div class="oc-card__top">
                        <h2 class="oc-card__name">{{ o.name }}</h2>
                        <span class="tm-chip" :class="tierChip(o.tier)">{{ tierLabel(o.tier) }}</span>
                    </div>
                    <p class="oc-card__summary" v-if="o.summary">{{ o.summary }}</p>
                    <span class="oc-card__meta tm-mono" v-if="o.postings">{{ o.postings }} postings</span>
                </router-link>
            </div>
            <div class="oc-pager" v-if="pages > 1">
                <button class="oc-pager__btn" :disabled="page <= 1" @click="load(page - 1)">Previous</button>
                <span class="tm-mono">{{ page }} / {{ pages }}</span>
                <button class="oc-pager__btn" :disabled="page >= pages" @click="load(page + 1)">Next</button>
            </div>
        </section>
    </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import LocationSelect from '@/components/LocationSelect.vue'
import { OccupationApiService } from '@/services/OccupationApiService'

const TIERS = {
    market: ['Market data', 'tm-chip--have'],
    reference: ['ESCO reference', 'tm-chip--accent'],
    emerging: ['Emerging', 'tm-chip--partial'],
}

export default {
    name: 'OccupationCatalog',
    components: { LocationSelect },
    setup() {
        const route = useRoute()
        const router = useRouter()
        const query = ref(route.query.q || '')
        const location = ref(route.query.location || 'Global')
        const category = ref(route.query.category || '')
        const categories = ref([])
        const items = ref([])
        const total = ref(0)
        const page = ref(Number(route.query.page) || 1)
        const perPage = ref(30)
        const loading = ref(true)
        const pages = computed(() => Math.ceil(total.value / perPage.value))
        const locQuery = computed(() => (location.value !== 'Global' ? { location: location.value } : {}))

        async function load(p = 1) {
            loading.value = true
            page.value = p
            router.replace({ query: { ...(query.value ? { q: query.value } : {}), ...locQuery.value,
                                      ...(category.value ? { category: category.value } : {}),
                                      ...(p > 1 ? { page: p } : {}) } })
            try {
                const res = await OccupationApiService.Catalog(query.value, location.value, p, category.value)
                items.value = res.data.occupations
                total.value = res.data.total
                perPage.value = res.data.per_page
            } finally {
                loading.value = false
            }
        }
        let timer = null
        function debouncedLoad() {
            clearTimeout(timer)
            timer = setTimeout(() => load(1), 250)
        }
        function loadCategories() {
            OccupationApiService.Categories(location.value).then(res => { categories.value = res.data.categories })
        }
        function onLocation() {
            loadCategories()
            load(1)
        }
        onMounted(async () => {
            loadCategories()
            await load(page.value)
        })
        return {
            query, location, category, categories, items, total, page, pages, loading, locQuery,
            load, debouncedLoad, onLocation,
            tierLabel: t => TIERS[t][0], tierChip: t => TIERS[t][1],
        }
    },
}
</script>

<style scoped>
.oc-head { padding-bottom: 28px; }
.oc-title { font-family: var(--tm-font-display); font-size: clamp(30px, 4vw, 44px); color: var(--tm-ink); margin: 0 0 22px; }
.oc-filters { display: flex; gap: 10px; flex-wrap: wrap; }
.oc-input {
    flex: 1 1 280px; padding: 12px 14px; border: 1px solid var(--tm-border); border-radius: var(--tm-radius-sm);
    background-color: var(--tm-surface); color: var(--tm-ink); font: inherit;
}
.oc-input:focus { outline: none; border-color: var(--tm-border-focus); box-shadow: 0 0 0 3px var(--tm-accent-glow); }
.oc-select { flex: 0 1 240px; padding-right: 36px; }
.oc-body { padding: 0 16px 64px; }
.oc-count { color: var(--tm-ink-muted); font-size: 14px; margin-bottom: 12px; }
.oc-empty { color: var(--tm-ink-muted); padding: 40px 0; }
.oc-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
.oc-card { display: flex; flex-direction: column; gap: 8px; padding: 18px; text-decoration: none; color: inherit; }
.oc-card__top { display: flex; justify-content: space-between; gap: 10px; align-items: flex-start; }
.oc-card__name { font-size: 17px; font-weight: 650; color: var(--tm-ink); margin: 0; }
.oc-card__summary { color: var(--tm-ink-soft); font-size: 14px; margin: 0; flex: 1;
    display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.oc-card__meta { color: var(--tm-ink-faint); font-size: 12px; }
.oc-pager { display: flex; justify-content: center; align-items: center; gap: 16px; margin-top: 28px; color: var(--tm-ink-soft); }
.oc-pager__btn { padding: 8px 16px; border: 1px solid var(--tm-border); border-radius: var(--tm-radius-sm);
    background: var(--tm-surface); color: var(--tm-ink); cursor: pointer; }
.oc-pager__btn:disabled { opacity: .45; cursor: default; }
.oc-card__name::first-letter { text-transform: uppercase; }
</style>
